import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion, useAnimation } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import voiture from "../assets/modern-electric-car.png"
import caseStudiesContent from '../data/caseStudiesContent'
import { X, PauseIcon, PlayIcon } from 'lucide-react';
import carPoints from '../data/card'
import carSound from '../assets/car-sound.mp3';


const frames = [
  {
    title: "Motorisation",
    description: "600 km d'autonomie",
    icon: "⚡"
  },
  {
    title: "Performance",
    description: "0-100 km/h en 3.2s",
    icon: "🏁"
  },
  {
    title: "Intelligence",
    description: "Conduite autonome",
    icon: "🧠"
  },
  {
    title: "Design",
    description: "Aérodynamisme optimal",
    icon: "✨"
  }
];



const Homepage = () => {
  const carControls = useAnimation();
  const textControls = useAnimation();
  const pointsControls = useAnimation();
  const [activeFilter, setActiveFilter] = useState('STRATEGIE');
  const [activePoint, setActivePoint] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(new Audio(carSound));
  const [isMounted, setIsMounted] = useState(false);

  const [showScroll, setShowScroll] = useState(true);
  const textContentRef = useRef(null);


  useEffect(() => {
    const targetNode = textContentRef.current;
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setShowScroll(false);
        } else {
          setShowScroll(true);
        }
      });
    }, options);

    if (targetNode) {
      observer.observe(targetNode);
    }

    return () => {
      if (targetNode) {
        observer.unobserve(targetNode);
      }
    };
  }, []);


  const textLines = [
    "L'innovation automobile",
    "redéfinie par",
    "l'intelligence",
    "et la",
    "technologie du futur."
  ];

  // Premier useEffect pour gérer le montage
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    let isSubscribed = true; // Pour éviter les fuites de mémoire

    const animateContent = async () => {
      if (!isMounted || !isSubscribed) return;

      try {
        const textAnimationDurationPerLine = 0.5;
        const totalTextLines = textLines.length;
        const totalTextDuration = textAnimationDurationPerLine * totalTextLines;

        // Réinitialiser les contrôles
        await Promise.all([
          textControls.set({ opacity: 0, y: 100 }),
          carControls.set({ x: 1000, opacity: 0 }),
          pointsControls.set({ scale: 0, opacity: 0 })
        ]);

        // Animation du texte
        const textAnimation = async () => {
          if (!isSubscribed) return;
          for (let i = 0; i < textLines.length; i++) {
            await textControls.start(index => ({
              y: index === i ? [100, 0] : index < i ? 0 : 100,
              opacity: index === i ? [0, 1] : index < i ? 1 : 0,
              transition: { duration: 0.5, ease: "easeOut" }
            }));
          }
        };

        // Animation de la voiture
        const carAnimation = async () => {
          if (!isSubscribed) return;
          await new Promise(resolve => setTimeout(resolve, (totalTextDuration - 2) * 1000));

          await carControls.start({
            x: [1000, 0],
            opacity: [0, 1],
            transition: { duration: 1.8, ease: "easeOut" }
          });

          if (!isSubscribed) return;

          await carControls.start({
            y: [0, -10, 0, -5, 0, -2, 0],
            transition: {
              duration: 1,
              times: [0, 0.2, 0.4, 0.6, 0.8, 0.9, 1],
              ease: "easeOut"
            }
          });

          if (!isSubscribed) return;

          await new Promise(resolve => setTimeout(resolve, 350));

          return pointsControls.start(i => ({
            scale: [0, 1],
            opacity: [0, 1],
            transition: {
              duration: 0.5,
              delay: i * 0.2,
              ease: "easeIn"
            }
          }));
        };

        await Promise.all([textAnimation(), carAnimation()]);
      } catch (error) {
        console.error('Animation error:', error);
      }
    };

    if (isMounted) {
      animateContent();
    }

    return () => {
      isSubscribed = false;
    };
  }, [isMounted, carControls, textControls, pointsControls, textLines.length]);

  // Modifiez l'animation des frames
  const frameVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: i => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15 + 2.5, // Commence après l'animation de la voiture
        duration: 0.4,
        ease: "easeOut"
      }
    })
  };

  // Fonction pour arrêter l'audio
  const stopAudio = () => {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
  };

  // Gestion de l'audio
  const handleAudioClick = (e) => {
    e.stopPropagation();

    if (isPlaying) {
      stopAudio();
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Nettoyage de l'audio
  useEffect(() => {
    const audio = audioRef.current;

    // Arrêter l'audio quand il se termine
    const handleEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('ended', handleEnded);

    // Nettoyage quand le composant est démonté
    return () => {
      stopAudio();
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  return (
    <Container>

      {/* FirstSection  */}
      <FirstSection>
        <ContentSection>
          {/* Text */}
          <TextContent>
            {textLines.map((line, index) => (
              <AnimatedText
                key={index}
                custom={index}
                initial={{ y: 100, opacity: 0 }}
                animate={textControls}
              >
                {line}
              </AnimatedText>
            ))}
          </TextContent>

          {/* Voiture  */}
          <CarContainer
            initial={{ x: 1000, opacity: 0 }}
            animate={carControls}
          >
            {/* Image Voiture  */}
            <CarImage
              src={voiture}
              alt="Voiture électrique moderne"
            />

            {/* Points Voiture  */}
            {carPoints.map((point, index) => (
              <React.Fragment key={point.id}>
                <InteractivePoint
                  onClick={() => setActivePoint(activePoint === point.id ? null : point.id)}
                  style={point.position}
                  active={activePoint === point.id}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={pointsControls}
                  custom={index}
                >
                  <PointDot />
                  <PointRing
                    initial={{ scale: 1, opacity: 1 }}
                    animate={{
                      scale: [1, 1.5],
                      opacity: [1, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear"
                    }} />
                </InteractivePoint>

                {activePoint === point.id && (
                  <InfoCard
                    style={point.position}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                  >
                    <CloseButton onClick={() => {
                      stopAudio();
                      setActivePoint(null);
                    }}>
                      <X size={20} />
                    </CloseButton>

                    <CardImage src={point.image} />
                    <CardContent>
                      <CardTitle>{point.title}</CardTitle>
                      <CardDescription>{point.description}</CardDescription>

                      {point.value && (
                        <CardValue>
                          {point.value}
                          <CardUnit>{point.unit}</CardUnit>
                        </CardValue>
                      )}

                      {point.hasAudio && (
                        <AudioButton
                          onClick={handleAudioClick}
                          isPlaying={isPlaying}
                        >
                          {isPlaying ? (
                            <>
                              <PauseIcon size={16} />
                              Arrêter le son
                            </>
                          ) : (
                            <>
                              <PlayIcon size={16} />
                              Écouter le son
                            </>
                          )}
                          <AudioWaveContainer isPlaying={isPlaying}>
                            {[...Array(8)].map((_, index) => (
                              <WaveLine
                                key={index}
                                duration={2 + Math.random()}
                                delay={index * 0.2}
                              />
                            ))}
                          </AudioWaveContainer>

                        </AudioButton>
                      )}

                    </CardContent>
                  </InfoCard>
                )}
              </React.Fragment>
            ))}
          </CarContainer>

        </ContentSection>

        {/* Ligne vertical */}
        <RoadSection>
          <Road />

          {/* 4 Card */}
          <InteractiveFramesContainer>
            {frames.map((frame, index) => (
              <InteractiveFrame
                key={index}
                custom={index}
                variants={frameVariants}
                initial="hidden"
                animate="visible"
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.98 }}
              >
                <FrameContent>
                  <FrameIcon>{frame.icon}</FrameIcon>
                  <TextContainer>
                    <FrameTitle>{frame.title}</FrameTitle>
                    <FrameDescription>{frame.description}</FrameDescription>
                  </TextContainer>
                </FrameContent>
              </InteractiveFrame>
            ))}
          </InteractiveFramesContainer>

        </RoadSection>

        {/* Scroll Icon */}
        <ScrollIndicator show={showScroll}>
          <ScrollText>Scroll</ScrollText>
          <ScrollIcon size={24} />
        </ScrollIndicator>
      </FirstSection>

      {/* SecondSection */}
      <SecondSection ref={textContentRef}>
        <ScrollTextContent >
          {/*    VOLTAUTO  */}
          <BackgroundText>  VOLTAUTO </BackgroundText>
          <LargeText>
            VOLTAUTO est une entreprise belge
            <HighlightText> d'innovation</HighlightText> dans le domaine de la
            <HighlightText> mobilité électrique</HighlightText>. Nous croyons en la
            fusion de la technologie avancée et du
            <HighlightText> design durable</HighlightText> pour créer
            les solutions de transport de demain.
          </LargeText>
        </ScrollTextContent>


        {/*  Case   */}
        <CaseStudiesSection >
          <FilterBar>
            <FilterButton
              active={activeFilter === 'STRATEGIE'}
              onClick={() => setActiveFilter('STRATEGIE')}
            >
              Stratégie
            </FilterButton>
            <FilterButton
              active={activeFilter === 'DESIGN'}
              onClick={() => setActiveFilter('DESIGN')}
            >
              Design Automobile
            </FilterButton>
            <FilterButton
              active={activeFilter === 'INGENIERIE'}
              onClick={() => setActiveFilter('INGENIERIE')}
            >
              Ingénierie
            </FilterButton>
            <FilterButton
              active={activeFilter === 'MATERIAUX'}
              onClick={() => setActiveFilter('MATERIAUX')}
            >
              Matériaux Avancés
            </FilterButton>
            <FilterButton
              active={activeFilter === 'PLATEFORMES'}
              onClick={() => setActiveFilter('PLATEFORMES')}
            >
              Plateformes
            </FilterButton>
            <FilterButton
              active={activeFilter === 'INDUSTRIALISATION'}
              onClick={() => setActiveFilter('INDUSTRIALISATION')}
            >
              Industrialisation
            </FilterButton>
          </FilterBar>

          <ProjectsWrapper>
            <ProjectCard>
              <TextCardContent>
                <ProjectTitle>
                  {caseStudiesContent[activeFilter].title}
                </ProjectTitle>
                <ProjectDescription>
                  {caseStudiesContent[activeFilter].description}
                </ProjectDescription>
              </TextCardContent>
              <ImageContent>
                <img
                  src={caseStudiesContent[activeFilter].image}
                  alt={caseStudiesContent[activeFilter].title}
                />
              </ImageContent>
            </ProjectCard>
          </ProjectsWrapper>
        </CaseStudiesSection>

      </SecondSection>
    </Container>
  );
};

export default Homepage;

const Container = styled.div`
  min-height: 100vh;
  background: white;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    overflow-x: hidden;
  }
`;

const FirstSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const ContentSection = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 60vh;
  gap: 2rem;

  @media (max-width: 1024px) {
    padding: 1rem;
    flex-direction: column;
    justify-content: center;
  }
`;

const TextContent = styled.div`
  padding: 4rem; 

  @media (max-width: 1024px) {
    width: 100%;
    text-align: center;
    padding: 2rem; 
  }
`;

const AnimatedText = styled(motion.span)`
  width: 100%;
  display: block;
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 0.5rem;
  color:  rgba(0, 0, 0, 0.8);

  @media (max-width: 768px) {
    font-size: clamp(1.8rem, 4vw, 2.5rem);
    margin-bottom: 0.3rem;
  }
`;

const CarContainer = styled(motion.div)`
  position: relative;
  width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 500px;
  }
`;

const CarImage = styled.img`
  width: 120%;
  height: auto;
  object-fit: contain;
  max-width: 1000px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const RoadSection = styled.div`
  width: 100%;
  position: relative;
  height: auto;
`;

const Road = styled.div`
  width: 100%;
  height: 2px;
  position: absolute;
  top: 50%; // Centre la ligne verticalement
  transform: translateY(-50%);
  background: linear-gradient(
    90deg,
    rgba(0,0,0,0.1) 0%,
    rgba(0,0,0,0.3) 50%,
    rgba(0,0,0,0.1) 100%
  );

  @media (max-width: 768px) {
    margin: 1rem 0;
  }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const ScrollIndicator = styled.div`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #666;

  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  
  @media (max-width: 768px) {
    bottom: 1rem;
  }
`;

const ScrollText = styled.span`
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const ScrollIcon = styled(ChevronDown)`
  animation: ${bounce} 2s infinite;
`;

//Pellicules ::::::
const InteractiveFramesContainer = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: auto;
  display: flex;
  gap: clamp(10px, 2vw, 20px);
  justify-content: center;
  padding: 0 2rem;

 
  @media (max-width: 1024px) {
    flex-direction: column;
    width: 100%;
    max-width: 600px;
    gap: 1rem;
     padding: 0 1rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 400px;
    gap: 1rem;
    padding: 0 1rem;
    bottom: 15%;
    overflow-x: auto;

  }
`;

const InteractiveFrame = styled(motion.div)`
  width: clamp(200px, 20vw, 280px);
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 2vw, 1.5rem);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  aspect-ratio: 16/9;


  @media (max-width: 1024px) {
    width: 100%;
    aspect-ratio: auto;
    min-height: 100px;
    flex-direction: row;
    padding: 1.5rem;
    text-align: left;
    transform: none;

    &:hover {
      transform: translateX(5px);
    }
  }
`;

const FrameContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
  }
`;

const FrameIcon = styled.div`
  font-size: clamp(1.2rem, 2vw, 1.5rem);
  margin-bottom: 0.5rem;
  color: #000;
  opacity: 0.8;

  @media (max-width: 768px) {
    margin-bottom: 0;
    font-size: 1.5rem;
    min-width: 40px;
  }
`;

const TextContainer = styled.div`
  text-align: center;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const FrameTitle = styled.h3`
  font-size: clamp(0.9rem, 1.5vw, 1.1rem);
  font-weight: 600;
  color: #000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FrameDescription = styled.p`
  font-size: clamp(0.8rem, 1.2vw, 0.9rem);
  color: #666;
  margin: 0.5rem 0 0 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-top: 0.25rem;
  }
`;


// SecondSection
const SecondSection = styled.section`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  flex-direction: column;
  gap: 4rem;
  

   @media (max-width: 1800px) {
    margin-top: 10vh; 
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
    margin-top: 10vh; 
  }
`;

const ScrollTextContent = styled.div`
   width: 100%;

  margin: 0 auto;
  opacity: 0;
  animation: fadeIn 1s ease-out forwards;
  animation-delay: 0.5s;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LargeText = styled.p`
  font-size: clamp(1.8rem, 3.5vw, 3.2rem);
  line-height: 1.3;
  font-weight: 300;
  color: #333;
  text-align: left;
  max-width: 85%;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: clamp(1.4rem, 5vw, 1.8rem);
    max-width: 100%;
    text-align: left;
    padding: 0 1rem;
  }
`;

const HighlightText = styled.span`
  font-weight: 600;
  color: black;
  position: relative;
  white-space: nowrap;
`;


const BackgroundText = styled.div`
  position: fixed;
  top: -10%;
  left: 80%;
  transform: translate(-50%, -50%);
  font-size: 10vw; // Plus grand pour être plus visible
  font-weight: 1000; // Plus épais
  color: rgba(0, 0, 0, 0.4);
  white-space: nowrap;
  pointer-events: none;
  z-index: 1; 
  user-select: none;
  text-transform: uppercase;
  letter-spacing: 1vw;
  opacity: 0.5;


     @media (max-width: 768px) {
    font-size: 7vw; 
    max-width: 100%;
  }
`;

// Case Image + Text ::::::
const CaseStudiesSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  margin-bottom: 40px;
  margin-top: 8vh;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
    margin-top: 4vh;
  }

`;

const FilterBar = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  overflow-x: auto;
  padding-bottom: 1rem;

  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; // Firefox
  
  &::-webkit-scrollbar {
    display: none; // Chrome, Safari, Edge
  }

  @media (max-width: 1024px) {
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 0.5rem 0;
    
    // Ajoute un indicateur de défilement horizontal
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(
        90deg,
        rgba(0,0,0,0.1) 0%,
        rgba(0,0,0,0.05) 100%
      );
    }
  }
`;

const FilterButton = styled.button`
  background: none;
  border: none;
  font-size: 0.9rem;
  color: ${props => props.active ? '#000' : '#666'};
  cursor: pointer;
  padding: 0.5rem 0;
  position: relative;
  white-space: nowrap;
  transition: color 0.3s ease;
  flex-shrink: 0;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${props => props.active ? '100%' : '0'};
    height: 1px;
    background: #000;
    transition: width 0.3s ease;
  }

  &:hover {
    color: #000;
  }

  &:hover:after {
    width: 100%;
  }

    @media (max-width: 768px) {
    padding: 0.5rem 0.25rem;
  }
`;

const ProjectsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ProjectCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  cursor: pointer;
  animation: fadeIn 0.5s ease-out;
  min-height: 400px;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 1024px) {
    gap: 1.5rem;
    min-height: 350px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const TextCardContent = styled.div`
  background: ${props => props.dark ? '#1a1a18' : '#d9d9d9'};
  padding: min(3rem, 5vw);
  display: flex;
  flex-direction: column;
  justify-content: center;
   border-radius: 4px;

 
  @media (max-width: 1024px) {
    padding: 2rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ImageContent = styled.div`
  background: #bab6b6;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: min(2rem, 4vw);
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
    &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    min-height: 250px;
    padding: 1rem;
  }
`;

const ProjectTitle = styled.h3`
  font-size: clamp(2rem, 3vw, 2.5rem);
  color: ${props => props.dark ? '#fff' : '#000'};
  margin: 0 0 clamp(0.8rem, 2vw, 1rem) 0;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const ProjectDescription = styled.p`
  color: ${props => props.dark ? '#aaa' : '#666'};
  font-size: clamp(0.9rem, 1.5vw, 1rem);
  line-height: 1.6;
  margin: 0;

   @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;


// Styles pour les points interactifs et les cartes
const InteractivePoint = styled(motion.button)`
  position: absolute;
  width: 40px;
  height: 40px;
  background: rgba(143, 189, 235, 0.6);
  border: 2px solid rgba(127, 186, 245, 0.8);
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease, background-color 0.3s ease;
  
  &:hover, &[data-active="true"] {
    background: white;
    transform: scale(1.1);
  }
`;

const PointDot = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background: white;
  border-radius: 50%;
`;

const PointRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 30px;
  height: 30px;
  border: 2px solid white;
  border-radius: 50%;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
    100% {
      transform: translate(-50%, -50%) scale(1.5);
      opacity: 0;
    }
  }
`;

const InfoCard = styled(motion.div)`
  position: absolute;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  width: min(90vw, 800px);
  min-height: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 30px rgba(0,0,0,0.1);
  z-index: 100;
  transform: translate(-50%, -50%);
  top: 2px;

  @media (max-width: 1700px) {
    width: 25vw;
    height: 100%;
    display: flex;
    flex-direction: column;
    
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    width: 70vw;
    min-height: auto;
    justify-content: center;

  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;


`;

const CardContent = styled.div`
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  background: #bfbdbd;
  color: white;


  @media (max-width: 2000px) {
   padding: 1.5rem;
  }

`;

const CardTitle = styled.h4`
  font-size: 2rem;
  font-weight: 500;
  margin: 0 0 1.5rem 0;
  color: black;
  opacity: 0.7;
  text-transform: uppercase;

  @media (max-width: 2000px) {
    font-size: 1.4rem;
    font-weight: 400;
    margin: 0 0 1rem 0;
  }
`;

const CardValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin: 0.5rem 0;
  color: black;
  opacity: 0.7;

  @media (max-width: 2000px) {
    font-size: 1.2rem;
    font-weight: 300;
    margin: 0;
  }

`;

const CardUnit = styled.span`
  font-size: 1rem;
  margin-left: 0.25rem;
  opacity: 0.7;
  color: black;
`;

const CardDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  color: black;
  opacity: 0.7;

  @media (max-width: 1700px) {
   line-height: 1.5;
   margin-bottom: 0.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: white;
  border-radius: 15px;
  opacity: 0.7;
  color: black;
  cursor: pointer;
  z-index: 10;
  padding: 0.2rem;
  
  &:hover {
    opacity: 0.5;
  }
`;


const vibrateAnimation = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-1px);
  }
  75% {
    transform: translateX(1px);
  }
`;


const AudioButton = styled.button`
    position: relative; 
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background:  #333;
    color: white;
    border: none;
    padding: 1rem 1.2rem;
    border-radius: 6px;
    margin-top: 1.5rem;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    min-width: 160px;
    overflow: hidden; 

    //Animation de vibration conditionnelle
    animation: ${props => props.isPlaying ? css`${vibrateAnimation} 0.2s ease-in-out infinite` : 'none'};

    span {
        display: inline-block;
        line-height: 1; 
    }

    &:hover {
        background: rgba(0, 0, 0, 0.7);
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
`;

const waveAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const AudioWaveContainer = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    pointer-events: none;
    opacity: ${props => props.isPlaying ? 1 : 0};
    transition: opacity 0.3s ease;
`;

const WaveLine = styled.div`
    width: 100%;
    height: 3px;
    background: linear-gradient(
        90deg,
        rgba(127, 186, 245, 0) 0%,
        rgba(127, 186, 245, 0.8) 70%,
        rgba(127, 186, 245, 0) 100%
    );
    animation: ${waveAnimation} ${props => props.duration}s linear infinite;
    animation-delay: ${props => props.delay}s;
`;

